import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { markSideChosen } from "@/components/ProtectedRoute";
import { Loader2, GraduationCap, HandCoins, ArrowRight, School } from "lucide-react";

const OPTIONS = [
  { key: "universities", label: "University side", icon: GraduationCap, desc: "Enter your marks, see the university courses your APS qualifies you for, and apply.", route: "/dashboard/marks", accent: "bg-[#0B2447]" },
  { key: "colleges", label: "College (TVET) side", icon: School, desc: "Browse NC(V) and N-Diploma programmes at public TVET colleges and apply.", route: "/dashboard/colleges", accent: "bg-[#B8860B]" },
  { key: "nsfas", label: "NSFAS Funding", icon: HandCoins, desc: "A free step-by-step guide to applying for NSFAS funding on the official platform.", route: "/dashboard/nsfas", accent: "bg-emerald-600" },
];

export default function TrackSelect() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(null);

  const choose = async (opt) => {
    setBusy(opt.key);
    try {
      await api.put("/track", { track: opt.key });
      markSideChosen();
      if (user) setUser({ ...user, track: opt.key });
      toast.success(`You're on the ${opt.label} path.`);
      navigate(opt.route);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]"><Header />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Choose your path</p>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-[#0B2447] mt-1" data-testid="track-title">Which side do you want to work on today?</h1>
        <p className="text-slate-600 mt-2">Universities and colleges are kept separate so nothing gets mixed up. Pick one side — you can use the <span className="font-semibold text-[#0B2447]">Switch side</span> button in the menu anytime. One once-off <span className="font-semibold text-[#0B2447]">R26.99</span> unlock covers every side.</p>

        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {OPTIONS.map((o) => {
            const Icon = o.icon;
            return (
              <button key={o.key} onClick={() => choose(o)} disabled={!!busy} data-testid={`track-${o.key}`}
                className={`text-left bg-white border rounded-2xl p-6 shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5 ${user?.track === o.key ? "border-[#0B2447] ring-2 ring-[#0B2447]/20" : "border-slate-200"}`}>
                <span className={`w-12 h-12 rounded-xl flex items-center justify-center ${o.accent}`}><Icon className="w-6 h-6 text-white" /></span>
                <h3 className="font-display font-bold text-xl text-[#0B2447] mt-4">{o.label}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{o.desc}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0B2447] mt-4">
                  {busy === o.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Choose <ArrowRight className="w-4 h-4" /></>}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
